import type { ReactNode } from 'react';
import type { FactCheckRead, FactCheckVerdictLabel } from '../../types/api';

/** Badge wording per verdict. Sans, because these are words, not data. */
const VERDICT_LABEL: Readonly<Record<FactCheckVerdictLabel, string>> = {
  supported: 'Supported',
  unsupported: 'Unsupported',
  uncertain: 'Uncertain',
};

/**
 * Fact-check + QA output for one variant.
 *
 * Strictly advisory (SRS §3 stage 3): every verdict here is a label, never a
 * gate. The user keeps approve/reject control whatever this section says, and
 * the copy states that outright rather than implying it.
 *
 * `lede` is overridable for the one other place these verdicts are read — the
 * operator's flag queue, where the reviewer's decision is long past and the
 * default wording would be addressed to nobody.
 */
export function FactCheckPanel({
  factCheck,
  lede = 'Fact check — advice only. Nothing here stops you approving this version.',
}: {
  factCheck: FactCheckRead | null;
  lede?: ReactNode;
}) {
  if (factCheck === null) {
    return (
      <div className="fact-check">
        <p className="fact-check__lede">Fact check has not run on this version.</p>
      </div>
    );
  }

  return (
    <div className="fact-check">
      <p className="fact-check__lede">{lede}</p>

      {factCheck.verdicts.length === 0 ? (
        <p className="fact-check__empty">Fact check — no facts to check.</p>
      ) : (
        <ul className="verdict-list">
          {factCheck.verdicts.map((verdict, index) => (
            <li
              className={`verdict verdict--${verdict.verdict}`}
              key={`${verdict.verdict}-${index}`}
            >
              <div className="verdict__head">
                <span className="verdict__badge">{VERDICT_LABEL[verdict.verdict]}</span>
                <span className="verdict__claim">{verdict.claim}</span>
              </div>
              {verdict.note ? <p className="verdict__note">{verdict.note}</p> : null}
              {verdict.sources.length > 0 ? (
                <ul className="verdict__sources">
                  {verdict.sources.map((source) => (
                    <li key={source.url}>
                      <a
                        className="verdict__source"
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {source.title || source.url}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
